/* eslint-disable react/forbid-component-props  */
import classNames from 'classnames';
import Leaflet, { divIcon, LatLngBounds } from 'leaflet';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { MAP_VIEW_CONFIG } from '../../brands/default/config';
import { MAP_VIEW } from '../../brands/default/constants';
import { trackEvent, trackPage } from '../../helpers/analytics';
import { formatCurrency } from '../../helpers/currency';
import {
  fetchCarouselDeals,
  getNewMapRelativeUrl,
} from '../../helpers/mapview';
import { redirectToURL } from '../../helpers/url';
import ThemeContext from '../../providers/ThemeProvider';
import Message from '../_generic/Message';
import MapDealsCarousel from './MapDealsCarousel';
import MapFilters from './MapFilters';
import SearchThisArea from './SearchThisArea';

/**
 * Don't use this directly as ssr is not supported by react-leaflet,
 * use WowMap which dynamically imports the component client side only
 */
const MapViewComponent = ({
  categoryFilters,
  centerLat,
  centerLon,
  isErrorApi,
  isLoadCategory,
  isLoading,
  isMissingFilter,
  isMobile,
  isNoResults,
  isSlowApi,
  pins,
  pageSize,
  selectedCategories,
  sliderValues,
  stackedPins,
  updateZoom,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const router = useRouter();
  const theme = useContext(ThemeContext);
  const [map, setMap] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [selectedPin, setSelectedPin] = useState();
  const [selectedDeal, setSelectedDeal] = useState();
  const [selectedStackedPin, setSelectedStackedPin] = useState('');
  const [zoomLevel, setZoomLevel] = useState(MAP_VIEW_CONFIG.MAP_ZOOM_DEFAULT);
  const [carouselDeals, setCarouselDeals] = useState();
  const [stackedCarouselDeals, setStackedCarouselDeals] = useState();
  const dealsCarouselChildRef = useRef(null);

  const handleCarouselSelectPin = useCallback((pinId, dealId) => {
    setSelectedPin(pinId);
    setSelectedDeal(dealId);
  }, []);

  const handleCarouselClose = useCallback(() => {
    setCarouselDeals([]);
    setStackedCarouselDeals([]);
    setSelectedStackedPin('');
  }, []);

  const handleStackedCarouselClose = useCallback(() => {
    setStackedCarouselDeals([]);
    setSelectedStackedPin('');
  }, []);

  const highlightFirstDeal = () => {
    // highlight the first deal
    if (pins && pins.length > 0) {
      const selectedId = pins[0].latLon[0].pinId;
      setSelectedPin(selectedId);
    }
  };

  const handleZoomEnd = () => {
    // highlight the first deal
    highlightFirstDeal();
  };

  const handleMapReady = () => {
    // highlight the first deal
    highlightFirstDeal();
  };

  const calculateBoundsFromPins = () => {
    const latLons = pins.map((pin) =>
      pin?.latLon?.length > 0 && pin?.latLon[0]?.lat
        ? [pin.latLon[0].lat, pin.latLon[0]?.lon]
        : null,
    );

    // get bounds and add a little padding so pins aren't on edges of map
    return new LatLngBounds(latLons).pad(0.01);
  };

  // add this event handler for inertial scrollwheel/mousepad behaviour
  // https://github.com/Leaflet/Leaflet/issues/4410
  let lastScroll = Date.now();
  Leaflet.Map.ScrollWheelZoom.prototype._onWheelScroll = function (event) {
    /* eslint-disable react/no-this-in-sfc, fp/no-this */
    if (Date.now() - lastScroll < 600) {
      event.preventDefault();

      return;
    }

    let delta = Leaflet.DomEvent.getWheelDelta(event);
    const debounce = this._map.options.wheelDebounceTime;

    if (delta >= -0.15 && delta <= 0.15) {
      event.preventDefault();

      return;
    }
    if (delta <= -0.25) delta = -0.25;
    if (delta >= 0.25) delta = 0.25;
    this._delta += delta;
    this._lastMousePos = this._map.mouseEventToContainerPoint(event);

    if (!this._startTime) {
      this._startTime = Date.now();
    }

    const left = Math.max(debounce - (Date.now() - this._startTime), 0);

    clearTimeout(this._timer);
    lastScroll = Date.now();
    this._timer = setTimeout(Leaflet.bind(this._performZoom, this), left);

    Leaflet.DomEvent.stop(event);
    /* eslint-enable react/no-this-in-sfc, fp/no-this */
  };

  // user starts a drag or zoom
  const handleUpdateStart = () => {
    setSelectedPin('');
    if (map) map.closePopup();
  };

  // update page using bounds object
  const updateBounds = (updatedBounds, newConfig) => {
    setSelectedPin('');
    setSelectedStackedPin('');
    setCarouselDeals([]);
    setStackedCarouselDeals([]);
    // need to reset selected pin because of zoom in
    highlightFirstDeal();
    if (!updatedBounds) return;
    let newCategories;
    let newSliderValues;
    if (newConfig) {
      if (newConfig?.categories) newCategories = newConfig.categories;
      if (newConfig?.sliderValues) newSliderValues = newConfig.sliderValues;
    }

    let newBounds = updatedBounds;
    if (!newBounds) {
      // when just arrived from local channel, need to calculate new bounds from pins
      newBounds = calculateBoundsFromPins();
    }
    const newUrl = getNewMapRelativeUrl({
      bottomRightLat: newBounds
        .getSouth()
        .toFixed(MAP_VIEW_CONFIG.MAP_LAT_LON_DP),
      bottomRightLon: newBounds
        .getEast()
        .toFixed(MAP_VIEW_CONFIG.MAP_LAT_LON_DP),
      categories: newCategories,
      filterPrice: MAP_VIEW.FILTER_PRICE_MAPS,
      pageSize,
      sliderValues: newSliderValues,
      topLeftLat: newBounds.getNorth().toFixed(MAP_VIEW_CONFIG.MAP_LAT_LON_DP),
      topLeftLon: newBounds.getWest().toFixed(MAP_VIEW_CONFIG.MAP_LAT_LON_DP),
    });

    redirectToURL(newUrl, router);
    if (newBounds) {
      setBounds(newBounds);
    }
  };

  const handlePinClick = async (latLon) => {
    // change selected slide without rerender of carousel
    dealsCarouselChildRef.current.setSlide(latLon.pinId);
    // close stacked carousel if it is open
    setStackedCarouselDeals([]);
    setSelectedPin(null);
    setSelectedDeal(null);
    trackEvent('mv_deal_pin_click');
    setSelectedPin(latLon.pinId);
    setSelectedStackedPin('');
  };

  const setFilters = (config) => {
    updateBounds(bounds, config);
  };

  useEffect(() => {
    trackPage({
      pageName: 'mapview',
    });
  }, []);

  useEffect(() => {
    setSelectedPin('');
    setSelectedStackedPin('');
    setCarouselDeals([]);
    setStackedCarouselDeals([]);
    if (pins && pins.length > 0) {
      setCarouselDeals(pins);
      highlightFirstDeal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pins]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (map) {
      map.options.minZoom = MAP_VIEW_CONFIG.MAP_ZOOM_MIN;
      map.options.maxZoom = MAP_VIEW_CONFIG.MAP_ZOOM_MAX;
      // these options don't work very well
      map.options.zoomDelta = MAP_VIEW_CONFIG.MAP_ZOOM_SNAP_DEFAULT;
      map.options.zoomSnap = MAP_VIEW_CONFIG.MAP_ZOOM_SNAP_DEFAULT;
      // doesn't work well with drag to search
      map.options.inertia = false;
      map.options.scrollWheelZoom = true;
      map.options.wheelDebounceTime = 400;
      map.options.updateWhenZooming = false;
    }

    if (map && pins && pins.length > 0) {
      let newBounds = null;
      if (!bounds) {
        // need to calculate new bounds from pins
        newBounds = calculateBoundsFromPins();
      }

      if (newBounds) {
        let newZoom = map.getBoundsZoom(newBounds);
        if (newZoom > zoomLevel) {
          trackEvent('MV_zoom_in');
        }
        if (newZoom < zoomLevel) {
          trackEvent('MV_zoom_out');
        }
        setZoomLevel(newZoom);
        updateZoom(newZoom);
        setBounds(newBounds);
        // set view from the newBounds, zoom in if not from a category page (local page)
        if (!isLoadCategory) {
          newZoom = Math.max(
            newZoom,
            MAP_VIEW_CONFIG.MAP_ZOOM_FIRST_LOAD_DEFAULT,
          );
        }
        map.setView(newBounds.getCenter(), newZoom);
      } else {
        // bounds were already set by dragging or previous request, no need to set view
        const newZoom = map.getBoundsZoom(bounds);
        if (newZoom > zoomLevel) {
          trackEvent('MV_zoom_in');
        }
        if (newZoom < zoomLevel) {
          trackEvent('MV_zoom_out');
        }
        setZoomLevel(newZoom);
        updateZoom(newZoom);
      }
    } else if (map && pins.length === 0 && centerLat && centerLon) {
      map.setView([centerLat, centerLon], MAP_VIEW_CONFIG.MAP_ZOOM_DEFAULT);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [bounds, centerLat, centerLon, pins, map]);

  // Logic if there are no pins available in the area and
  // no center lat and lon to render the map.
  useEffect(() => {
    const { bottomRightLat, bottomRightLon, topLeftLat, topLeftLon } =
      router.query;

    if (
      map &&
      pins.length === 0 &&
      bottomRightLat &&
      bottomRightLon &&
      topLeftLat &&
      topLeftLon
    ) {
      const newLatLons = [
        [topLeftLat, topLeftLon],
        [bottomRightLat, bottomRightLon],
      ];
      const newCenterBounds = new LatLngBounds(newLatLons);
      map.fitBounds(newCenterBounds);
      // map.setView(newCenter, MAP_ZOOM_DEFAULT);
    }
  }, [map, pins.length, router]);

  return (
    <>
      <MapFilters
        categoryFilters={categoryFilters}
        isMobile={isMobile}
        selectedCategories={selectedCategories}
        setFilters={setFilters}
        sliderValues={sliderValues}
      />
      {carouselDeals?.length > 0 && (
        <div
          className={classNames({
            'carousel-container__hidden': stackedCarouselDeals?.length > 0,
          })}
        >
          <MapDealsCarousel
            deals={carouselDeals}
            isMobile={isMobile}
            onClose={handleCarouselClose}
            // selectedPinId={selectedPin}
            ref={dealsCarouselChildRef}
            setSelectedPin={handleCarouselSelectPin}
            showClose={false}
          />
        </div>
      )}
      {stackedCarouselDeals?.length > 0 && (
        <MapDealsCarousel
          deals={stackedCarouselDeals}
          isMobile={isMobile}
          onClose={handleStackedCarouselClose}
        />
      )}
      <div>
        <MapContainer
          className="wow-map"
          scrollWheelZoom
          tap={false}
          whenCreated={(leafletMap) => setMap(leafletMap)}
          whenReady={handleMapReady}
        >
          {isErrorApi && (
            <div className="message-container">
              <Message
                iconPosition="left"
                message={MAP_VIEW_CONFIG.MAP_API_ERROR_TEXT}
                zIndex={0}
              />
            </div>
          )}
          <SearchThisArea
            isLoading={isLoading}
            isMissingFilter={isMissingFilter}
            isNoResults={isNoResults}
            isSlowApi={isSlowApi}
            pageSize={pageSize}
            selectedCategories={selectedCategories}
            sliderValues={sliderValues}
            updateBounds={updateBounds}
            updateStart={handleUpdateStart}
            zoomEnd={handleZoomEnd}
          />
          <TileLayer
            attribution='Maps &copy; <a href="https://www.thunderforest.com/">Thunderforest</a>, Data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.thunderforest.com/atlas/{z}}/{x}/{y}.png?apikey=ded142062c184099b57f4a9ee090a0bf"
          />
          {pins.map((pin) => {
            const {
              category,
              currency,
              dealIid,
              latLon,
              price,
              stacked,
              subCategory,
            } = pin;

            return (
              <React.Fragment key={dealIid}>
                {latLon.map((ll, index) => (
                  <React.Fragment key={dealIid + index}>
                    {!stacked && (
                      <Marker
                        eventHandlers={{
                          click: async () => {
                            handlePinClick(ll);
                          },
                        }}
                        icon={divIcon({
                          html: `<div class=${
                            (selectedDeal === dealIid ||
                              selectedPin === ll?.pinId) &&
                            !stackedCarouselDeals?.length > 0
                              ? 'selected'
                              : ''
                          }><div class="map-view-pin"><i class="map-view-pin-icon ${
                            category.shortName
                          } ${
                            subCategory.shortName
                          }"></i><div class="map-view-price">${formatCurrency(
                            currency,
                            price,
                          )}</div></div></div>`,
                        })}
                        key={dealIid + index}
                        position={[ll.lat, ll.lon]}
                        zIndexOffset={
                          (selectedDeal === dealIid ||
                            selectedPin === ll?.pinId) &&
                          !stackedCarouselDeals?.length > 0
                            ? '1000'
                            : ''
                        }
                      />
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
          {stackedPins.map((item, index) => (
            <React.Fragment key={index}>
              <Marker
                eventHandlers={{
                  click: async () => {
                    // setSelectedPin('');
                    if (
                      selectedStackedPin ===
                      item?.pinId.slice(item?.pinId.indexOf('_'))
                    ) {
                      setSelectedStackedPin('');
                    } else {
                      setSelectedStackedPin(
                        item?.pinId.slice(item?.pinId.indexOf('_')),
                      );
                      const deals = await fetchCarouselDeals(item, true);
                      setStackedCarouselDeals(deals);
                    }
                  },
                }}
                icon={divIcon({
                  html: `<div class=${
                    selectedStackedPin ===
                    item?.pinId.slice(item?.pinId.indexOf('_'))
                      ? 'selected'
                      : ''
                  }><div class="map-view-pin"><i class="map-view-pin-icon map-stacked-pin"></i></div></div>`,
                })}
                key={index}
                position={[item.lat, item.lon]}
                zIndexOffset={
                  selectedStackedPin ===
                  item?.pinId.slice(item?.pinId.indexOf('_'))
                    ? '1000'
                    : ''
                }
              />
            </React.Fragment>
          ))}
        </MapContainer>
      </div>
      <style jsx>{`
        :global(.wow-map) {
          height: 100vh;
          margin-bottom: 20px;
        }
        :global(.map-view-pin) {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 20px 20px 2px;
          transform: rotate(45deg);
          border: 1.2px solid white;
          transform-origin: 100% 100%;
          background-color: ${theme.colors.primary};
          box-shadow: 2px 0 4px rgba(0, 0, 0, 0.25);
        }
        :global(.map-view-pin-icon) {
          background-size: 20px 25px;
          background-repeat: no-repeat;
          background-position: 0 1px;
          transform: scaleX(-1) rotate(-135deg);
          height: 20px;
          width: 20px;
        }
        // STACKED
        :global(.map-view-pin-icon.map-stacked-pin) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M583 371a104 104 0 0 1 105 104 104 104 0 0 1-105 104 104 104 0 0 1-104-104 104 104 0 0 1 104-104m0 396c161 0 292-131 292-292 0-219-292-542-292-542 0 0-291 323-291 542a292 292 0 0 0 291 292m-375-292c0-187 212-444 250-492l-41-50c0 0-292 323-292 542 0 132 88 244 208 280-76-69-125-169-125-280z" horiz-adv-x="1000" /></svg>');
        }
        // CATS
        // activities
        :global(.map-view-pin-icon.activities) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M563 743l81-163c11-23 34-40 60-43l180-26c65-10 91-90 44-136l-131-127c-18-18-27-44-23-70l31-179c11-65-57-115-115-84l-161 85c-23 12-51 12-74 0l-161-85c-58-31-126 19-115 84l31 179c4 26-4 52-23 70l-130 127c-47 46-21 126 44 136l180 26c26 3 48 20 59 43l81 163c29 59 113 59 142 0z m-163-540c-47 0-86-38-86-86 0-48 39-86 86-86 48 0 87 38 87 86 0 48-39 86-87 86z m-50-62c-5 0-9 4-9 8 0 5 4 9 9 9 4 0 8-4 8-9 0-4-4-8-8-8z m7 24c0 5 4 9 9 9 5 0 8-4 8-9 0-5-3-9-8-9-5 0-9 4-9 9z m14-30c-5 0-8 3-8 8 0 5 3 9 8 9 5 0 9-4 9-9 0-4-4-8-9-8z m47 302c0 2 1 3 1 5 5 15 11 32 4 49-7 13-19 25-34 30-22 6-50-6-60-27-10-18-4-38 0-57h89z m-5-16l-80 1c2-8 3-16 4-24h72c1 8 3 16 4 23z m0-80c-4 13-5 27-5 41l-69 1c0-8 0-16 0-24-4-52-22-31-40-106-10-40-4-79 6-108 12 41 50 71 95 71 18 0 35-5 50-13 0 16-1 34-4 53-5 30-24 55-33 85z m-194 55c14 0 28 0 42 1 16 1 28-3 36-17 8-14 19-16 35-15 63 4 189 8 253 10 28 1 37 11 36 40-1 51-36 83-69 114-17 17-37 32-55 49-25 23-26 57-7 88 6 11 19 12 30 7 14-5 20-19 14-31-8-19-1-27 13-39 46-39 93-78 116-136 9-24 13-48 7-73-2-13 3-15 14-16 29-1 36 2 38 24 0 10 8 11 15 7 25-12 49-24 74-37 9-4 5-8-1-12-23-13-46-28-69-42-7-4-12-6-15 4-3 8-3 21-13 22-13 2-26 0-39 0-6-1-6-6-4-11 22-63-3-114-40-164-21-27-47-49-69-75-8-9-9-16-4-27 8-16 6-29-6-36-12-8-26-5-37 8-20 23-20 60 1 83 14 14 27 29 42 43 28 27 53 57 65 96 8 25 8 50-12 72-5 6-11 8-18 7-65-3-192-8-257-9-21-1-35-10-43-27-4-8-9-11-18-12-25-1-50-2-75-4-4 0-15-1-12 3 2 4 4 7 5 10 4 12 16 36 35 38 5 0 20 1 20 5-2 5-15 4-23 5-16 3-25 19-35 32-6 6-8 11 4 13 9 1 17 1 26 2z" horiz-adv-x="1000" /></svg>');
        }
        // beauty
        :global(.map-view-pin-icon.beauty) {
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M83 78c0 48 0 96 0 144-1 21 8 30 29 29 61 0 122 0 183 1 22 0 29-10 29-31 0-98 0-196 0-294 0-21-9-30-29-30-61 1-122 1-183 0-22 0-29 10-29 31 0 50 0 100 0 150z m211 316c0-32 0-65 0-97 1-19-8-27-26-27-43 1-85 0-128 0-20 0-28 10-27 30 0 64 0 127 0 191 0 19 6 29 27 28 42-1 84-2 125-1 22 0 30-8 30-30-1-31-1-62-1-94 0 0 0 0 0 0z m-77 410c29-34 47-64 48-100 2-46 0-92 1-138 0-21-10-28-29-27-16 0-16 1-33 1h-4c-16 0-16-1-33-1-19-1-29 6-28 27 1 46-1 92 1 138 1 36 19 66 48 100 0 0 12 23 29 0z m648 10c-2-30 19-37 4-94-64-169-131-337-197-506-14-34-27-69-41-103-4-9-8-17-14-29-25 27-46 51-72 79 104 216 209 434 314 652 6-1 0 2 6 1z m-221-564c3 0 6 1 9 2 18 14 10 29-3 28-4-1-7-1-10-1-18-10-10-25 4-29z m-150-105c64-1 108-49 107-118-1-73-58-133-124-132-59 1-110 58-109 121 1 73 57 129 126 129z m-67-131c-1-35 22-63 50-64 34 0 68 40 68 80-1 35-23 64-51 64-34-1-67-40-67-80z m493-1c0-68-46-118-109-117-68 0-122 61-121 136 1 66 46 113 111 113 72-1 119-53 119-132z m-56-1c0 43-32 82-67 82-28 0-48-26-48-64 0-44 29-79 65-80 28-1 51 27 50 62z m-271 280c-14 34-33 74-45 105-41 104-81 208-123 312-14 35-10 68 5 103 6-10 13-21 18-32 54-112 108-225 163-337 27-57 30-98-18-151z m108-45c14-28 34-63 46-86-25-27-46-50-67-74-39 45-13 97 21 160z" horiz-adv-x="1000" /></svg>');
        }
        // entertainment
        :global(.map-view-pin-icon.entertainment) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M713-100h-412c-16 0-29 11-32 26l-103 497c-1 7 1 13 5 18 4 5 10 8 16 8h645c7 0 13-3 17-8 4-5 5-12 4-18l-108-497c-3-15-17-26-32-26z m-526 533c-2 0-4-1-4-2-1 0-2-2-1-5l102-497c2-8 9-13 17-13h412c8 0 15 5 17 13l108 498c0 2-1 4-1 4-1 1-3 3-5 3h-645v-1z m199 1h71v-533h-71l0 533z m139 1h70v-534h-70l0 534z m138 1h71v-534h-71l0 534z m125 5h58l-58-291v291z m-533 0h70v-533h-44l-27 128 1 405z m-84 42l-2 21c-3 38 27 71 65 71v13c0 30 25 55 56 55v24c0 37 30 67 67 67 0 20 16 37 36 37h23c0 26 22 48 49 48h0c24 0 45-18 48-42l1-8c0 25 21 45 46 45 25 0 45-20 45-45v-30c36 0 66-29 66-66v-4h23c32 0 58-26 58-58 28 0 50-23 50-51v-3c28 0 49-22 48-49l-1-25" horiz-adv-x="1000" /></svg>');
        }
        // fitness
        :global(.map-view-pin-icon.fitness) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M237 110c-40 46-80 92-121 138-13 16-12 36 3 50 10 9 20 17 29 26 17 16 40 12 54-4 37-43 75-86 113-128 41-48 83-96 125-144 13-15 14-32 3-47-1-2-3-4-5-5-9-8-19-17-28-25-18-15-39-14-54 4-40 45-79 90-119 135z m446 393c-40 46-80 91-120 137-11 12-12 29-5 41 2 4 5 7 8 9 10 10 21 19 31 28 15 12 35 11 49-3 2-3 5-5 7-8 78-89 156-177 233-266 8-9 13-18 11-30-1-9-5-17-12-23-10-9-20-18-30-27-15-12-38-11-50 4-41 46-81 92-122 138z m-463-408c27-31 54-63 81-93 16-17 14-41-3-54-10-8-19-17-28-25-16-13-37-12-51 3-24 27-47 53-70 80-32 37-64 73-97 110-14 16-13 39 4 52 10 9 20 17 29 26 16 13 37 12 51-3 28-32 56-64 84-96z m649 570c28-31 54-63 82-93 16-17 13-42-3-54-10-7-18-16-28-24-17-15-37-14-53 4-26 30-53 61-80 91-28 33-57 65-85 98-11 12-13 28-5 42 0 0 0 0 0 0 16 14 31 29 48 41 12 8 29 3 40-10 25-27 49-55 73-83 4-4 8-8 11-12z m-229-147c3-4 6-8 9-11 13-15 26-30 39-44 4-5 4-7 0-11-76-66-152-132-227-198-30-26-60-52-89-79-5-4-7-3-11 1-14 17-29 34-44 51-3 3-3 5 0 8 107 93 214 187 320 281 1 0 1 1 3 2z m285 118c-2 3-4 4-5 6-23 26-45 52-68 78-3 3-4 6 0 9 8 6 16 13 24 20 5 5 10 5 15-1 21-24 43-48 64-72 3-4 4-9 0-13-9-9-20-17-30-27z m-846-586c1-2 3-2 4-4 23-26 46-53 69-79 3-4 4-6 0-9-7-5-13-11-19-17-6-4-11-4-16 2-21 24-42 48-63 72-4 4-4 9-1 12 8 8 17 15 26 23z" horiz-adv-x="1000" /></svg>');
        }
        // healthcare
        :global(.map-view-pin-icon.healthcare) {
          background-position: 0 0;
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M446-66l-353 329c-8 8-16 16-23 25h170c44 0 84 26 101 67l20 49 96-214c8-16 24-27 42-27s35 10 43 26l83 166 3-7c19-37 57-61 98-61h204c-7-8-15-16-23-24l-353-329c-14-14-34-22-54-22s-39 8-54 22z m538 447h-258c-6 0-11 4-14 9l-45 90c-8 16-25 26-42 26s-34-10-42-26l-81-161-100 222c-7 17-25 28-43 28s-36-12-43-29l-62-149c-2-6-8-10-14-10h-209c-5 0-9-1-14-2-11 31-17 64-17 98v12c0 136 99 252 233 275 89 15 180-14 244-78l23-23 23 23c64 64 155 93 244 78 134-23 233-139 233-275v-12c0-33-5-65-16-96z" horiz-adv-x="1000" /></svg>');
        }
        // restaurantandfood, drinking
        :global(.map-view-pin-icon.restaurantandfood),
        :global(.map-view-pin-icon.drinking) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M415 798c2 1 3 2 4 2 18 3 20 1 20-17 0-52 0-105 0-157 0-4 0-7 1-11 0-5 3-7 7-7 5 0 7 3 8 7 1 3 1 6 1 9 0 52 0 104 0 156 0 2 0 4 0 6 1 4-3 11 3 13 6 2 14 1 21 0 1 0 3-4 3-7 1-18 1-36 1-54 1-25 1-50 1-76 1-38 2-77 2-116 0-7 0-15-2-23-6-25-23-40-47-49-1 0-3-1-4-1-1-2-2-4-2-7 1-23 2-46 3-69 1-18 1-36 2-54 1-35 3-70 4-106 1-27 2-55 3-83 2-35 3-70 4-106 2-28 3-56 4-84 1-10 2-20 2-30 0-23-13-41-34-50-20-7-44 3-53 19-6 8-8 17-8 28 1 27 2 54 3 81 1 27 2 54 3 81 1 20 1 39 2 59 1 35 3 70 4 104 1 30 3 59 4 88 1 34 2 68 4 102 0 6 1 12 1 18 0 9 0 9-8 12-16 5-28 15-37 30-6 11-9 23-9 36 1 43 1 87 2 130 1 39 1 78 2 117 0 11 1 11 11 11 4 0 8 0 12 0 1 0 2-1 4-2 0-3 0-7 0-11 0-54 0-109 0-163 0-4 0-7 1-11 1-3 4-6 8-6 5 1 7 3 8 7 0 4 0 7 0 10 0 55 0 110 0 166 0 10 1 10 11 10 2 0 4 0 5 0 6 0 7-1 8-7 0-4 0-7 0-10 0-53 0-106 0-159 0-3 0-7 1-10 1-4 3-7 8-7 4 1 7 3 7 8 1 3 1 6 1 10 0 53 0 107 0 160 0 5 0 9 0 13z m252-176c0-48-1-95 0-143 0-78 1-157 2-235 0-59 1-117 0-175 0-40 0-80-1-119-1-15-2-31-8-45-4-11-12-19-24-21-12-2-21 5-28 14-9 12-13 26-17 40-5 22-7 45-9 69-2 25 0 49 3 74 2 20 6 40 9 60 3 22 6 45 8 67 2 15 3 30 5 45 2 15 2 30-1 45-2 9-7 17-12 25-9 15-19 30-28 46-12 22-16 46-16 71 0 40 0 79 2 119 3 49 9 97 22 145 7 28 17 54 37 76 6 6 14 12 21 17 11 7 20 4 27-7 4-6 5-13 5-21 0-41 1-82 1-123 0-8 0-16 0-24 0 0 1 0 2 0z" horiz-adv-x="1000" /></svg>');
        }
        // spascountryhouse
        :global(.map-view-pin-icon.spascountryhouse) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M290 366h-52v-96h52v96z m473 1h-52v-97h52v97z m-264 36c23 0 42 18 42 41 0 23-19 42-42 42-23 0-41-19-41-42 0-23 18-41 41-41z m2 230l-158-136h-280c-16 0-27 16-22 31l83 234c3 9 12 15 22 15h709c10 0 18-6 21-15l83-234c5-15-6-30-22-30h-278l-158 135z m341-172h-188l-153 124-146-124h-194c-20 0-36-16-36-36v-482c0-19 16-35 36-35h207 270 204c20 0 36 16 36 35v482c0 20-16 36-36 36z m-168-79c0 12 10 22 22 22h78c13 0 23-10 23-22v-124c0-12-10-22-23-22h-78c-12 0-22 10-22 22v124z m-174 138c43 0 77-35 77-77 0-43-34-77-77-77-42 0-77 34-77 77 0 42 35 77 77 77z m-298-139c0 13 10 22 22 22h79c12 0 22-9 22-22v-124c0-12-10-22-22-22h-79c-12 0-22 10-22 22v124h0z m253-423c-91-52-202-20-254 72-5 8-9 17-13 26-1 1 0 5 1 6 20 18 41 32 66 42 1 0 1-1 1-1 37-93 106-138 206-141-3-2-5-3-7-4z m-39 30c-96 20-154 103-148 198 1 9 2 17 3 25 1 1 3 4 4 4 41 7 80 4 118-13 1 0 2-1 3-1-19-87 7-157 73-216-19-2-36 0-53 3z m80 3c-46 34-77 78-84 136-7 54 10 101 45 143 12 14 27 27 43 38 102-68 121-200 35-289-9-10-20-18-30-27-3-3-6-3-9-1z m57 15c42 45 62 98 57 160-1 10-3 20-5 30-1 4 0 6 4 7 37 16 76 19 115 12 2 0 4-2 4-4 18-95-29-187-123-217-20-7-41-9-61-10-4 0-8 1-10 1 6 6 13 14 19 21z m259 49c-28-63-74-106-143-119-46-9-90 1-132 26 101 3 170 48 207 143 9-5 19-9 27-14 13-9 26-18 38-27 4-3 5-5 3-9z" horiz-adv-x="1000" /></svg>');
        }
        // sportsadventure
        :global(.map-view-pin-icon.sportsadventure) {
          background-position: 1px -1px;
          height: 18px;
          width: 18px;
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" height="18" width="18" viewBox="0 0 650 650"><!--!Font Awesome Free 6.5.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2023 Fonticons, Inc.--><path fill="white" d="M247.5 25.4c-13.5 3.3-26.4 7.2-38.6 11.7C142.9 61.6 96.7 103.6 66 153.6c-18.3 29.8-30.9 62.3-39.2 95.4L264.5 486.6c13.5-3.3 26.4-7.2 38.6-11.7c66-24.5 112.2-66.5 142.9-116.5c18.3-29.8 30.9-62.3 39.1-95.3L247.5 25.4zM495.2 205.3c6.1-56.8 1.4-112.2-7.7-156.4c-2.7-12.9-13-22.9-26.1-25.1c-58.2-9.7-109.9-12-155.6-7.9L495.2 205.3zM206.1 496L16.8 306.7c-6.1 56.8-1.4 112.2 7.7 156.4c2.7 12.9 13 22.9 26.1 25.1c58.2 9.7 109.9 12 155.6 7.9zm54.6-331.3c6.2-6.2 16.4-6.2 22.6 0l64 64c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0l-64-64c-6.2-6.2-6.2-16.4 0-22.6zm-48 48c6.2-6.2 16.4-6.2 22.6 0l64 64c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0l-64-64c-6.2-6.2-6.2-16.4 0-22.6zm-48 48c6.2-6.2 16.4-6.2 22.6 0l64 64c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0l-64-64c-6.2-6.2-6.2-16.4 0-22.6z"/></svg>');
        }
        // tradesmen
        :global(.map-view-pin-icon.tradesmen) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M458 848c-62-6-110-29-138-66-31-41-43-93-43-191 0-49 1-66 10-97 18-63 56-117 110-152 42-28 85-41 118-37 47 8 99 37 138 80 35 39 56 83 67 141 4 24 4 100 0 140-9 79-35 126-87 156-37 22-107 32-175 26z m92-92c11-3 21-5 21-6 1-1-12-36-14-39-1-1-11 0-22 2-10 3-26 4-35 4-9 0-25-1-36-4-11-2-20-3-21-2-1 1-5 10-9 20-5 14-6 18-4 19 2 2 34 9 47 11 14 2 54-1 73-5z m-2-103c30-7 53-20 85-46 16-14 21-17 26-17 9 0 17 6 25 19l8 10-1-38c-2-54-7-81-24-117-23-49-65-90-113-113-18-8-42-14-54-14-34 0-85 26-120 61-21 20-35 41-47 66-17 36-22 63-24 117l-1 38 7-10c9-13 17-19 25-19 6 0 10 3 26 17 38 31 64 43 104 50 18 3 60 1 78-4z m-224-345c-108-58-154-85-167-97-27-24-57-97-70-173l-3-19 5-10c7-14 32-39 50-51 7-4 22-13 33-19l22-11 5 5c3 3 7 6 10 7 4 2 4 3-5 9-20 13-25 37-12 60 8 15 29 37 45 46 40 24 87 25 129 4 12-6 20-12 33-25l17-18h84 85l12 14c13 15 24 23 41 31 38 19 86 17 125-6 15-9 37-31 45-46 7-14 9-29 3-42-3-8-15-20-21-22-3-1-2-2 7-9l10-7 21 10c11 6 26 15 33 19 18 12 43 37 50 50 5 11 5 11 3 26-4 22-17 75-24 95-8 22-23 53-33 66-14 19-26 27-93 65-71 40-127 69-131 69-3 0-13-5-23-12-21-14-37-21-57-27-8-3-16-6-18-8-4-2-10-16-20-43-8-21-15-39-15-39-1 0-8 18-16 39-10 27-16 41-19 43-2 2-11 5-18 8-21 6-36 13-57 27-10 7-21 12-23 12-3 0-22-10-43-21z m-40-275c-23-5-42-18-54-37-12-17-12-17 37-17 33 0 44 0 45-2 2-3 3-46 1-59l-1-11h-42c-51 0-52 0-42-16 13-19 36-34 61-39 16-3 39 0 56 8 15 7 32 24 41 39l5 10h109 109l4-9c10-20 34-39 58-46 38-10 79 5 100 38 10 15 10 15-40 15l-43 1-1 6c-2 7-2 51 0 58l1 6 43 1c51 0 51 0 39 17-10 15-28 29-45 34-16 6-41 6-58 1-21-6-43-24-53-44l-5-10h-109-109l-7 12c-18 34-62 54-100 44z" horiz-adv-x="1000" /></svg>');
        }
        // ukcitybreaks
        :global(.map-view-pin-icon.ukcitybreaks) {
          height: 18px;
          width: 18px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M926-91v312h-337v-312h-59v371h198v334h-66v131h-74v105h-59v-105h-75v-131h-65v-705h-59v574h-256v-574h-74v-59h1000v59z m-332 633h59v-59h-59z m0-131h59v-59h-59z m-364-479h-58v58h58z m0 140h-58v58h58z m0 140h-58v59h58z m0 140h-58v59h58z m292 0h-58v59h58z m0 131h-58v59h58z m7 190h59v-59h-59z m192-764h-59v235h59z m131 0h-58v235h58z" horiz-adv-x="1000" /></svg>');
        }
        // ukseaside
        :global(.map-view-pin-icon.ukseaside) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M625 78c89 13 188 22 292 22v-167h-709c0 27 134 90 334 130v270c-35-10-65-33-84-64-25 42-72 71-125 71-53 0-99-29-125-71 2 149 146 271 334 288v1a42 42 0 0 0 41 42 42 42 0 0 0 42-42v-1c188-17 332-139 333-288-25 42-72 71-125 71-53 0-99-29-125-71-18 31-48 54-83 65v-256m-333 689a208 208 0 0 0-209-209v209h209z" horiz-adv-x="1000" /></svg>');
        }
        // SUB CATS
        // adventure-theme-parks-zoos
        :global(.map-view-pin-icon.adventure-theme-parks-zoos) {
          height: 18px;
          width: 18px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M986 850c-12 0-21-4-28-12-16-18-12-44 8-57 3-2 8-4 10-4 2-1 7-1 12-1 8 0 11 1 16 4 15 8 23 27 19 42-6 18-19 28-37 28z m-266-64c-9 0-19-4-26-11-9-9-14-25-10-36 4-12 13-22 25-27 8-2 14-2 21 0 17 5 29 21 28 38 0 9-5 19-11 25-7 7-17 11-27 11z m302-47c-5 0-13-1-17-1-5-1-15-2-23-3-89-8-87-8-91-12l-4-3 1-12c0-6 1-16 2-21 1-6 2-17 2-25 1-8 2-18 3-23 0-4 1-10 1-12 0-5 3-10 6-12 3-1 6-1 21 1 11 1 24 2 48 4 7 1 18 2 25 3 6 0 18 1 26 2 16 1 21 3 23 8 2 3 1 15-1 35-1 5-2 16-2 23-4 42-4 42-8 45-2 2-4 3-12 3z m-243-50c-3 0-7-1-12-4-4-2-11-5-15-6-3-2-9-5-13-7-4-1-10-4-13-6-3-1-9-4-13-5-4-2-10-5-13-6-4-2-9-5-13-6-4-2-10-5-13-6-4-2-10-4-14-6-5-3-7-5-9-7-2-5-2-6 5-20 2-5 4-11 6-15 5-10 8-18 12-25 1-4 4-9 5-13 10-21 11-22 15-24 5-4 5-3 27 7 4 1 10 4 13 6 3 1 9 4 13 5 4 2 10 5 13 6 3 2 9 5 13 6 4 2 10 5 13 6 3 2 9 4 13 6 4 2 11 5 15 7 5 2 9 5 11 6 2 3 3 9 1 13-1 1-3 6-5 10-5 13-9 21-13 29-2 4-4 10-6 13-1 3-4 9-6 13-2 4-4 9-6 13-3 7-6 10-11 10z m-280-35c-10 0-12-1-16-3-8-4-15-11-18-18-4-9-4-23-1-31 5-8 11-15 19-18 6-3 9-4 15-4 16 0 28 7 35 21 3 6 3 8 3 16 0 12-3 19-11 27-8 8-13 10-26 10z m569-84c-52 0-102-4-141-13-14-3-36-8-39-9-2-1-6-2-9-2-19-6-54-19-72-27-2-1-7-3-10-4-10-5-29-14-37-19-5-2-11-6-15-8-3-1-7-4-9-5-2-2-4-3-4-3-2 0-44-27-62-40-6-4-23-17-37-28-47-35-85-63-90-67-12-9-40-27-52-34-13-7-38-20-40-20 0 0-3-1-6-2-36-17-93-26-116-20-18 4-35 17-56 40-6 6-9 10-21 25-6 8-7 9-17 22-31 40-54 67-75 86-9 8-28 23-36 27-17 10-38 19-53 22-17 3-22 4-46 4h-25l0-47 1-48 18 0 19 0v-237-237h-19-19v-38-38h550 550v38 38h-19-19v275 275l18 0 19 0 0 47 1 47z m-504-2c-3 0-5-1-11-5-3-3-8-7-11-9-26-20-46-36-51-43-18-26-20-57-4-83 7-12 25-33 28-33 3-1 8 0 9 1 1 1 7 6 14 11 7 5 14 11 16 13 3 2 12 9 20 15 9 7 24 19 34 27 10 8 19 16 20 17 5 7 4 9-16 34-7 8-14 17-16 20-12 16-26 33-29 34-1 0-2 1-3 1z m420-97c2 0 2-35 2-273v-272h-57-57v259c0 245 0 259 2 259 1 1 5 2 8 3 10 4 22 7 32 10 2 0 6 1 9 2 8 2 27 6 40 8 4 1 11 2 14 3 3 0 6 1 7 1z m-189-64c1 0 1-86 1-241v-240h-57-56v203 203l13 10c26 20 65 46 86 58 10 6 12 7 13 7z m-680-55c1 0 5-4 13-14 3-3 6-7 6-7 0 0 2-3 5-6 2-3 5-6 5-7 1-1 8-10 15-19 16-20 20-26 25-32 2-2 5-6 7-8 6-7 16-18 26-28l11-10v-147-148h-57-57v213c0 117 0 213 1 213z m491-79c0 0 1-78 1-174v-173h-57-57l0 140 1 140 20 10c11 6 22 12 24 13 2 1 4 3 4 3 2 0 35 21 57 37 3 2 6 4 7 4z m-189-92v-128-127h-57-57v126 125l5 0c2-1 9-2 15-3 19-3 46-1 78 4 3 1 8 2 11 2z" horiz-adv-x="1000" /></svg>');
        }
        // afternoon-tea
        :global(.map-view-pin-icon.afternoon-tea) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M368 586l13-2c79-15 161-15 240 0l10 2-15 29c-18 33-52 56-89 60l6 11c10 17 5 39-11 50l-1 0c-13 10-30 10-43 0-16-11-20-33-10-50l6-10-1 0c-33-5-63-24-81-52l-24-38z m551-67l-12 11c-35 35-90 39-129 9l-9-7c-17-12-25-32-22-52 2-16-14-28-29-22-27 10-41 40-33 68l8 25-41 10-21-4c-91-19-185-18-275 1l-14 3-42-13c18-57 9-119-23-169l-25-37c-33 0-61 26-62 60l-2 62c-2 52-45 93-98 92l-50-1v-4c0-8 5-15 13-18 23-6 39-26 40-49l4-111c5-130 86-244 206-292l13-5c0-19 12-35 30-40l45-14c66-19 136-20 202-3l46 11c18 5 30 21 30 40v3l252 258c51 53 50 136-2 188z m-44-176l-107-104v23c0 40-10 79-30 114l1 0c4 19 20 31 39 31 11 0 22 3 26 9 4 6 1 14-5 21-12 15-13 36-4 53 13 23 41 31 64 20 26-13 44-38 49-67 6-37-7-74-33-100z" horiz-adv-x="1000" /></svg>');
        }
        // bus-tours
        :global(.map-view-pin-icon.bus-tours) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M140 229c-32 0-59-27-59-60 0-32 27-59 59-59 33 0 60 27 60 59 0 33-27 60-60 60z m0-93c-18 0-33 15-33 33 0 19 15 34 33 34 19 0 34-15 34-34 0-18-15-33-34-33z m595 93c-32 0-59-27-59-60 0-32 27-59 59-59 33 0 60 27 60 59 0 33-27 60-60 60z m0-93c-18 0-33 15-33 33 0 19 15 34 33 34 19 0 34-15 34-34 0-18-15-33-34-33z m208 236c7 0 12 5 12 12 0 6-5 12-12 12 0 0 0 0 0 0l0 99c0 52-43 95-95 95h-685c-30 0-56-21-63-50l-35-144c-2-10 5-19 15-19h15l-7-85c-21 0-37-17-37-38v-58c-1 0-2 0-3 0-6 0-12-5-12-12 0-7 6-12 12-12 1 0 2 0 3 1v-10c0-3 2-6 6-6 3 0 6 3 6 6v13c0 43 34 77 76 77 43 0 77-35 77-77v-5c0-12 10-22 22-22h409c7 0 13 6 13 12v10c0 42 33 75 75 75 41 0 75-33 75-75v-9c0-7 5-13 13-13l117 0c1-1 2-1 3-1 7 0 12 6 12 12 0 7-5 12-12 12l0 200c0 0 0 0 0 0z m-721-63c0-7-6-13-14-13h-89c-8 0-14 7-13 15l7 53c1 6 6 11 13 11h82c8 0 14-6 14-14v-52z m3 172c0-8-6-14-13-14h-90c-8 0-14 7-13 15l3 23c3 23 23 41 47 41h53c7 0 13-6 13-14v-51h0z m21 51c0 8 6 14 14 14h80c8 0 14-6 14-14v-51c0-8-6-14-14-14h-80c-8 0-14 6-14 14v51z m110-223c0-7-6-13-13-13h-81c-7 0-13 6-13 13v52c0 8 6 14 13 14h81c7 0 13-6 13-14v-52z m134 0c0-7-7-13-14-13h-81c-7 0-13 6-13 13v52c0 8 6 14 13 14h81c7 0 14-6 14-14v-52z m0 172c0-8-6-14-13-14h-81c-8 0-14 6-14 14v51c0 8 6 14 14 14h81c7 0 13-6 13-14v-51z m136-172c0-7-6-13-14-13h-80c-8 0-14 6-14 13v52c0 8 6 14 14 14h80c8 0 14-6 14-14v-52z m0 172c0-8-6-14-14-14h-80c-8 0-14 6-14 14v51c0 8 6 14 14 14h80c8 0 14-6 14-14v-51z m28 51c0 8 6 14 13 14h81c7 0 13-6 13-14v-51c0-8-6-14-13-14h-81c-7 0-13 6-13 14v51z m108-223c0-7-6-13-13-13h-81c-8 0-14 6-14 13v52c0 8 6 14 14 14h81c7 0 13-6 13-14v-52z m41 158c-7 0-13 6-13 14v51c0 8 6 14 13 14h53c24 0 44-18 47-41l3-23c1-8-5-15-13-15h-90z m124-293h-86v201h86v-201z" horiz-adv-x="1000" /></svg>');
        }
        // massage
        :global(.map-view-pin-icon.massage) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M90 454c6-2 12-5 18-7 34-12 66-12 102-10 25 2 50 3 76 4 3 0 7 0 11 0 19 0 37-1 55-5 2 0 3 0 5 0 12-3 23-7 32-16 4-7 7-14 9-21 0-2 1-3 1-4 2-7 3-12 3-20 6 4 11 8 17 12 1 1 3 2 4 3 2 1 3 2 5 3 2 2 2 2 4 3 4 3 4 3 7 6 5 3 9 4 14 5 2 0 4 1 6 1 2 0 4 1 7 1 2 0 4 1 6 1 16 2 31 4 48 5-5-8-10-15-15-21-18-24-38-54-34-85 2-13 7-20 17-29 13-8 25-7 39-4 23 7 47 16 65 31 4 2 4 2 10 2 1-1 3-3 4-4 11-12 23-13 39-14 0-1 0-3 0-4 3-20 8-35 23-48 1-1 2-2 3-3 19-15 43-14 66-14 2 0 3 0 5 0 23 1 46 1 69 7 1 0 2 1 4 1 29 8 54 21 77 40 15 12 33 21 50 30 9 6 19 11 28 17 10 6 10 6 16 6 0-74 0-147 0-223-9-1-17-2-26-3-2 0-3 0-5 0-5 0-10-1-14-1-2 0-4 0-5 0-53-3-106-5-159-4-1 0-2 0-3 0-81 1-161 6-240 21-30 5-54-10-80-23-1 0-2-1-3-1-3-1-5-3-8-4-27-13-55-25-84-36-2-1-4-2-7-3-51-20-104-38-159-50-2 0-5-1-7-1-15-3-29-3-44-3-2 0-4 0-6 0-15 0-31 1-43 11-12 12-12 24-12 41 2 59 29 125 56 177 1 0 2 0 4 0 5-7 7-15 10-24 5-17 11-26 27-34 14-7 26-8 41-4 11 5 20 11 29 19 4 4 4 4 8 6-1-9-2-17-5-26-1-2-1-3-2-5 0-1 0-3-1-4 57 22 121 49 158 101 0 1 1 2 2 3 1 1 2 2 2 3-16 0-32-1-48-2-8 0-15 0-23-1-7 0-14 0-22-1-2 0-5 0-8 0-14 0-25-1-35-11-4-5-8-9-12-14-3-2-5-5-8-7-6-5-12-10-18-16-22-20-22-20-39-20-9 3-14 7-19 16-1 3-2 6-3 10-10 31-10 31-32 55-4 2-9 3-14 5-6 2-6 2-10 5-6 15 0 33 3 48 1 2 1 4 2 5 0 2 0 4 1 5 1 15-4 29-9 43-1 2-2 4-3 6-2 6-4 13-7 19-2 7-4 13-7 20-1 4-3 8-4 12-1 2-1 4-2 6-1 1-1 3-2 5-1 4-1 4-1 10z m777 125c-9-12-19-22-30-33-2-2-4-3-5-5-4-4-8-8-12-11-4-5-9-10-14-15-4-4-9-9-13-13-2-2-4-4-6-5-9-10-19-19-29-28-35-30-35-30-48-73 0-3 0-3 0-5-3-23-41-51-58-66-5-4-9-5-16-6-7 2-7 2-13 6-4 6-5 9-5 17 2 9 8 15 14 22 1 1 2 3 4 4 3 4 6 8 9 11-6-4-12-9-18-14-26-21-52-39-82-53-2-1-2-1-4-2-13-6-25-10-39-7-5 7-5 12-4 20 12 48 51 84 92 110 36 21 36 21 47 21 0 1 1 2 1 4 3 5 5 10 9 14 8 12 15 23 20 37 38 93 114 79 200 70z m119-88c1-17 2-34 3-51 0-8 0-16 1-23 3-51 3-51-8-65-11-9-24-14-37-19-7-3-13-7-20-11-5-3-11-6-16-9-15-9-29-17-42-28-16-13-34-21-53-26-2-1-3-1-4-2-22-6-45-7-68-7-4 0-8 0-12 0-17 0-32 1-46 10-6 6-9 13-11 21 5 27 43 48 64 62 30 20 53 29 90 23 15-2 15-2 28 3 5 5 5 5 9 11 5 6 11 10 17 15 7 6 13 12 20 19 2 2 5 5 8 8 2 1 3 3 5 4 11 11 22 21 33 31 2 2 2 2 4 4 3 3 7 6 10 9 1 1 2 2 3 3 19 18 19 18 22 18z m-859 107c6-1 10-3 14-6 2-1 3-2 4-3 2-1 3-2 4-3 48-34 102-40 158-47 56-7 102-16 146-52-2-27-11-47-31-65-4 4-8 9-11 14-16 20-42 26-67 30-46 5-94 2-140-2-32-2-75-4-102 16-12 11-15 24-17 39 1 32 22 56 42 79z m139 84c5-4 7-9 10-14 5-8 11-12 19-17 1-1 3-2 5-3 14-8 30-13 46-17 35-11 75-25 94-58 5-13 5-28 1-41-7 2-13 5-20 8-19 10-40 15-61 19-2 0-3 1-5 1-22 4-45 7-67 9-19 3-38 5-57 10-2 0-3 0-4 1-24 5-45 15-65 30-2 2-2 2-5 3-3 3-7 6-10 9 5 18 22 34 38 44 23 11 55 22 81 16z m-172-328c4-8 4-12 2-21 0-2-1-4-1-6 0-2-1-4-1-6-4-21-7-41 3-61 6-7 11-11 18-15-4-13-8-25-14-38 0-1-1-3-2-5-2-4-3-8-5-12-18 0-36 21-48 33-14 15-31 33-31 55 0 16 11 25 22 35 12 12 26 21 39 31 2 2 2 2 4 3 9 7 9 7 14 7z m299 330c12-13 21-28 21-45 0-2 0-4 0-6-2 1-5 2-7 3-21 9-41 17-63 24-21 6-40 13-53 32 0 1 0 2 0 4 37 9 70 9 102-12z" horiz-adv-x="1000" /></svg>');
        }
        // motoring
        :global(.map-view-pin-icon.motoring) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M108 597c-51 1-81 0-85-2-4-1-9-7-10-10-2-5-2-33-2-142l0-115 2-5c3-5 4-7 7-8 1-1 2-2 3-2 0 0 2 3 4 6 13 26 29 46 50 62 23 16 49 27 78 31 9 1 31 1 40 0 10-2 21-4 30-7 4-1 8-2 8-2 0 0-44 133-57 172-6 15-7 17-14 21l-3 1z m248 0c-27 0-60-11-84-27-14-9-30-22-37-30l-3-4 29-85 28-85 6-6c22-24 36-53 42-88 1-8 1-31 0-42-1-10-6-30-10-40 0-1 1-1 9-1 5-1 66-1 135-1 120 0 125 0 125 1-8 20-11 39-11 60 0 27 5 49 16 72 9 18 17 30 32 44 32 32 71 48 116 48 17 0 29-2 46-7 33-10 63-31 85-60 2-4 6-8 7-11l2-4h39c28 0 39 0 39 1 0 0-2 4-5 7-56 67-134 118-210 140-30 9-55 13-88 15-18 1-21 1-26-1-3-2-13-11-22-21-15-19-62-76-63-78l-2-2h-86-85l-1 95c0 86 0 95-1 97-2 5-6 8-11 11-4 2-5 2-11 2z m84-21c-9 0-18-1-18-1-1-1-1-16-2-78l0-64 57 1 57 0 2 3c3 7 5 16 6 25 0 5 1 9 1 10l1 2-33 1c-18 0-34 1-36 1-4 1-8 4-10 8l-3 4 0 15c-1 8 0 15 0 18 1 5 6 11 10 13 4 1 14 2 33 2 8 0 15 1 15 1 1 0-1 3-6 7-14 15-29 24-49 30-7 2-7 2-25 2z m-270-205c-15 0-21-1-34-6-41-14-70-46-81-89-2-7-2-9-2-26 0-21 0-24 5-39 13-36 39-63 75-77 10-4 19-6 29-7 2 0 9 0 15 0 25 1 45 7 65 20 37 25 58 67 54 112-3 46-33 87-76 104-17 7-30 9-50 8z m578 0c-15 0-18 0-24-1-22-5-38-13-53-26-29-24-44-53-45-89-1-17 1-30 6-45 7-23 24-45 47-62 14-10 35-18 55-21 7 0 27 0 35 2 26 4 48 16 67 35 18 19 28 39 33 65 2 10 1 31-1 42-3 17-8 31-17 45-16 24-39 41-64 49-14 5-20 6-39 6z m159-80c-1-1 0-4 1-7 6-28 4-58-5-86-2-5-3-10-3-10 0 0 17 0 38 0h39l3 2c5 2 8 5 11 9l2 4v36 36l-2 3c-1 2-3 5-4 7-6 5-5 5-45 5-19 1-35 1-35 1z m-161-1c5 0 10-1 16-3 7-2 10-4 14-9 11-11 15-27 11-41-5-14-15-24-31-27-6-2-8-2-15-1-13 3-24 11-29 22-4 7-5 15-5 22 2 14 10 27 23 33 5 2 11 4 16 4z m-571 0c14 0 25-6 33-19 5-6 7-11 7-19 1-13-3-22-12-31-6-6-11-9-18-11-5-1-16-1-21 0-10 2-19 9-25 19-3 4-4 8-4 15-2 14 2 24 11 34 8 8 17 12 29 12z" horiz-adv-x="1000" /></svg>');
        }
        // spa
        :global(.map-view-pin-icon.spa) {
          background-position: 0 0;
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M90-127c0 17 1 24 3 33 8 41 37 73 91 100 27 14 55 24 103 38 14 4 28 9 32 10 14 7 27 20 34 34 7 15 8 20 8 62v36l2 3 3 4 9-4c18-9 51-19 74-22 46-8 87-5 125 10 7 3 12 5 12 5 1 0 1-14 1-31 1-34 2-38 8-51 11-23 28-35 64-44 32-8 58-18 86-32 68-34 113-79 125-125 3-10 3-15 4-30l0-19h-392-392z m384 329c-25 2-53 9-80 21-15 6-17 7-17 10 0 5-3 16-7 22-1 4-12 18-23 32-11 14-32 40-46 58l-25 32 4 5c9 12 13 27 12 42-2 15-14 89-16 95-4 11-12 25-20 35l-5 5 7 9c31 40 93 87 170 130l16 8 42 0c42-1 47-1 67-6 22-5 44-15 63-28 17-11 27-19 39-33 23-25 38-53 46-84l3-12-5-6c-6-8-12-20-13-28-1-4-4-26-7-51-5-33-6-46-6-53 1-10 4-20 9-27 1-2 2-5 2-5 0-1-18-22-39-47-21-25-40-48-42-51-9-12-12-23-13-38l0-9-14-8c-23-11-43-16-68-18-15-2-19-2-34 0z m-164 6c-4 1-195 147-204 155-2 2-6 6-7 8-1 3-19 74-39 159-21 85-43 175-49 201-6 25-11 48-11 51 0 6 4 13 10 15 4 2 72 14 78 14 6 0 11-2 14-7 2-4 4-11 13-62 6-32 11-62 13-68 3-16 9-29 18-42 4-5 46-59 79-102 15-20 14-16 22-64 3-24 6-45 6-48 0-11-8-19-18-20-10 0-17 4-21 13-1 2-6 27-10 55-5 29-9 53-10 54-1 2-6 7-9 9-4 2-13 1-17-1-5-3-9-8-10-13-1-3 14-95 17-111 3-13 13-27 25-34 10-7 19-10 31-11l10 0 46-59c26-32 48-59 49-62 3-5 3-14 0-20-4-9-17-14-26-10z m327 3c-4 3-7 11-7 22 0 9 0 10 4 16 4 7 26 34 59 74l26 30 5-1c12-2 31 4 40 13 9 9 19 31 22 48 4 19 14 89 14 95 0 9-1 15-6 19-9 9-22 5-28-9-4-8-3-4-12-60-7-46-9-53-13-60-2-4-10-8-15-8-8 0-14 10-14 21 0 10 9 78 11 86 5 14 5 14 52 75 38 48 39 49 46 61 10 19 12 29 24 104 5 33 9 60 10 61 1 5 6 11 9 12 2 1 6 1 9 1 7 0 64-8 71-10 2-1 6-4 9-6 4-4 5-5 5-10 0-4-19-85-46-204-51-222-48-208-61-222-8-7-191-145-199-149-5-2-11-2-15 1z m94 379c-8 19-19 40-32 57-41 55-101 89-172 97l-6 0 9 5c30 14 99 42 131 54l10 4 5-3c14-10 33-27 47-43 26-32 45-70 53-111l2-11-22-28c-12-16-22-29-22-29 0 0-2 4-3 8z m-517 17c-7 9-16 20-19 25l-7 9 2 11c8 37 24 71 46 101 9 11 30 32 42 42 28 23 66 41 100 48 25 6 30 6 89 7 72 1 100-1 128-8 13-4 31-10 29-11 0 0-6-2-12-5-17-6-54-22-81-34-142-65-259-140-299-195-3-3-5-6-5-6-1 0-6 7-13 16z" horiz-adv-x="1000" /></svg>');
        }
        // spa-breaks
        :global(.map-view-pin-icon.spa-breaks),
        :global(.map-view-pin-icon.spabreaks) {
          background-position: 0 0;
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M90-127c0 17 1 24 3 33 8 41 37 73 91 100 27 14 55 24 103 38 14 4 28 9 32 10 14 7 27 20 34 34 7 15 8 20 8 62v36l2 3 3 4 9-4c18-9 51-19 74-22 46-8 87-5 125 10 7 3 12 5 12 5 1 0 1-14 1-31 1-34 2-38 8-51 11-23 28-35 64-44 32-8 58-18 86-32 68-34 113-79 125-125 3-10 3-15 4-30l0-19h-392-392z m384 329c-25 2-53 9-80 21-15 6-17 7-17 10 0 5-3 16-7 22-1 4-12 18-23 32-11 14-32 40-46 58l-25 32 4 5c9 12 13 27 12 42-2 15-14 89-16 95-4 11-12 25-20 35l-5 5 7 9c31 40 93 87 170 130l16 8 42 0c42-1 47-1 67-6 22-5 44-15 63-28 17-11 27-19 39-33 23-25 38-53 46-84l3-12-5-6c-6-8-12-20-13-28-1-4-4-26-7-51-5-33-6-46-6-53 1-10 4-20 9-27 1-2 2-5 2-5 0-1-18-22-39-47-21-25-40-48-42-51-9-12-12-23-13-38l0-9-14-8c-23-11-43-16-68-18-15-2-19-2-34 0z m-164 6c-4 1-195 147-204 155-2 2-6 6-7 8-1 3-19 74-39 159-21 85-43 175-49 201-6 25-11 48-11 51 0 6 4 13 10 15 4 2 72 14 78 14 6 0 11-2 14-7 2-4 4-11 13-62 6-32 11-62 13-68 3-16 9-29 18-42 4-5 46-59 79-102 15-20 14-16 22-64 3-24 6-45 6-48 0-11-8-19-18-20-10 0-17 4-21 13-1 2-6 27-10 55-5 29-9 53-10 54-1 2-6 7-9 9-4 2-13 1-17-1-5-3-9-8-10-13-1-3 14-95 17-111 3-13 13-27 25-34 10-7 19-10 31-11l10 0 46-59c26-32 48-59 49-62 3-5 3-14 0-20-4-9-17-14-26-10z m327 3c-4 3-7 11-7 22 0 9 0 10 4 16 4 7 26 34 59 74l26 30 5-1c12-2 31 4 40 13 9 9 19 31 22 48 4 19 14 89 14 95 0 9-1 15-6 19-9 9-22 5-28-9-4-8-3-4-12-60-7-46-9-53-13-60-2-4-10-8-15-8-8 0-14 10-14 21 0 10 9 78 11 86 5 14 5 14 52 75 38 48 39 49 46 61 10 19 12 29 24 104 5 33 9 60 10 61 1 5 6 11 9 12 2 1 6 1 9 1 7 0 64-8 71-10 2-1 6-4 9-6 4-4 5-5 5-10 0-4-19-85-46-204-51-222-48-208-61-222-8-7-191-145-199-149-5-2-11-2-15 1z m94 379c-8 19-19 40-32 57-41 55-101 89-172 97l-6 0 9 5c30 14 99 42 131 54l10 4 5-3c14-10 33-27 47-43 26-32 45-70 53-111l2-11-22-28c-12-16-22-29-22-29 0 0-2 4-3 8z m-517 17c-7 9-16 20-19 25l-7 9 2 11c8 37 24 71 46 101 9 11 30 32 42 42 28 23 66 41 100 48 25 6 30 6 89 7 72 1 100-1 128-8 13-4 31-10 29-11 0 0-6-2-12-5-17-6-54-22-81-34-142-65-259-140-299-195-3-3-5-6-5-6-1 0-6 7-13 16z" horiz-adv-x="1000" /></svg>');
        }
        // activity-breaks
        :global(.map-view-pin-icon.activitybreaks) {
          background-position: 0 0;
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M128 448h96c18 0 32 14 32 32s-14 32-32 32h-96c-18 0-32-14-32-32s14-32 32-32zm672 0h96c18 0 32 14 32 32s-14 32-32 32h-96c-18 0-32-14-32-32s14-32 32-32zm-480-64c35 0 64 29 64 64s-29 64-64 64-64-29-64-64 29-64 64-64zm384 0c35 0 64 29 64 64s-29 64-64 64-64-29-64-64 29-64 64-64zm-192-256c53 0 96 43 96 96v384c0 53-43 96-96 96s-96-43-96-96V224c0-53 43-96 96-96zm-64 96v384c0 18 14 32 32 32s32-14 32-32V224c0-18-14-32-32-32s-32 14-32 32z"/><circle fill="white" cx="320" cy="320" r="24"/><circle fill="white" cx="704" cy="320" r="24"/><circle fill="white" cx="320" cy="704" r="24"/><circle fill="white" cx="704" cy="704" r="24"/></svg>');
        }
        // theatrecircusshows
        :global(.map-view-pin-icon.theatrecircusshows) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M513 588c3-6 7-12 10-18 9-21 19-41 28-62 8-21 14-43 20-65 7-23 11-46 15-70 5-34 6-68 5-102-1-20-4-40-7-60-6-29-14-57-26-84-15-31-35-60-62-82-24-19-50-31-80-36-36-5-71 0-105 14-32 13-61 32-88 55-18 16-34 32-49 51-19 23-36 48-52 75-16 28-29 57-41 88-10 26-18 53-24 81-4 19-7 39-11 59 0 1 0 3 0 4 179-20 334 31 467 152z m-138-488c18 0 33 3 49 9 18 8 34 19 47 35 15 20 22 42 23 66 1 1 0 4-1 4-2 1-4 1-5-1-4-4-8-9-12-13-17-18-38-32-62-40-19-7-39-12-59-14-18-2-36-2-54 0-5 0-10 1-16 1-1 1-3-1-4-2 0 0 0-3 1-4 6-5 12-11 19-16 22-16 47-25 74-25z m-127 257c-21 0-38-6-53-17-21-16-32-36-31-63 1-8 3-16 8-23 1-2 3-2 5-3 0 0 2 2 2 4 7 23 23 40 44 52 24 14 50 18 78 15 1 0 3 0 3 1 1 1 1 3 0 4-1 2-2 4-4 5-14 17-33 25-52 25z m211 64c-3 0-6 0-9 0-2 0-4 0-6-1-24-4-44-16-57-38-6-10-9-22-8-35 0-1 1-3 2-4 1 0 3 1 4 2 20 18 43 30 70 34 25 2 47-3 67-17 1-1 3-1 4-2 1 2 2 4 2 5 0 11-4 20-10 28-15 20-35 28-59 28z m307-221c11 9 23 19 33 29 29 26 53 57 74 89 17 27 31 55 44 85 10 24 19 48 26 73 5 19 9 38 13 57 4 15 5 30 8 45 0 5 1 9 1 14-87-14-172-8-256 17-83 25-158 67-223 126-33-63-55-129-66-200 36 23 69 49 98 79 5-8 9-15 13-22 11-20 21-41 29-63 1-2 3-4 4-4 7-3 14-6 21-8 17-5 34-6 51-4 2 1 5 1 7 1 1-5 1-5-1-9-13-17-30-26-51-28-4 0-8-1-12-1 2-6 4-12 5-18 5-20 10-40 13-60 3-20 5-41 7-62 1-8 1-8 9-10 1 0 2 0 3 0 22-4 40-13 57-28 23-21 35-48 38-79 0-1 0-3-1-4-1 0-3 0-5 1-1 0-2 1-3 2-10 11-21 22-33 30-16 12-35 19-54 25-3 1-7 2-10 2-3-21-5-42-9-62-4-21-10-40-18-60 23-6 47-8 70-4 78 17 118 51 118 51z m99 248c-9-17-24-29-43-34-8-3-16-3-21-4-34 1-59 13-75 39-6 10-10 22-9 34 0 2 1 3 2 4 1 1 3 0 4-1 17-14 35-25 56-30 16-4 32-4 48-1 13 3 26 9 37 17 1 1 3 2 3 2 1-2 2-3 2-5 1-7-1-14-4-21z" horiz-adv-x="1000" /></svg>');
        }
        // seaside
        :global(.map-view-pin-icon.seaside) {
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M625 78c89 13 188 22 292 22v-167h-709c0 27 134 90 334 130v270c-35-10-65-33-84-64-25 42-72 71-125 71-53 0-99-29-125-71 2 149 146 271 334 288v1a42 42 0 0 0 41 42 42 42 0 0 0 42-42v-1c188-17 332-139 333-288-25 42-72 71-125 71-53 0-99-29-125-71-18 31-48 54-83 65v-256m-333 689a208 208 0 0 0-209-209v209h209z" horiz-adv-x="1000" /></svg>');
        }

        // citybreaks
        :global(.map-view-pin-icon.citybreaks) {
          height: 18px;
          width: 18px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M926-91v312h-337v-312h-59v371h198v334h-66v131h-74v105h-59v-105h-75v-131h-65v-705h-59v574h-256v-574h-74v-59h1000v59z m-332 633h59v-59h-59z m0-131h59v-59h-59z m-364-479h-58v58h58z m0 140h-58v58h58z m0 140h-58v59h58z m0 140h-58v59h58z m292 0h-58v59h58z m0 131h-58v59h58z m7 190h59v-59h-59z m192-764h-59v235h59z m131 0h-58v235h58z" horiz-adv-x="1000" /></svg>');
        }
        // countryhousehotels
        :global(.map-view-pin-icon.countryhousehotels) {
          background-position: 0 0;
          height: 19px;
          width: 19px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M290 366h-52v-96h52v96z m473 1h-52v-97h52v97z m-264 36c23 0 42 18 42 41 0 23-19 42-42 42-23 0-41-19-41-42 0-23 18-41 41-41z m2 230l-158-136h-280c-16 0-27 16-22 31l83 234c3 9 12 15 22 15h709c10 0 18-6 21-15l83-234c5-15-6-30-22-30h-278l-158 135z m341-172h-188l-153 124-146-124h-194c-20 0-36-16-36-36v-482c0-19 16-35 36-35h207 270 204c20 0 36 16 36 35v482c0 20-16 36-36 36z m-168-79c0 12 10 22 22 22h78c13 0 23-10 23-22v-124c0-12-10-22-23-22h-78c-12 0-22 10-22 22v124z m-174 138c43 0 77-35 77-77 0-43-34-77-77-77-42 0-77 34-77 77 0 42 35 77 77 77z m-298-139c0 13 10 22 22 22h79c12 0 22-9 22-22v-124c0-12-10-22-22-22h-79c-12 0-22 10-22 22v124h0z" horiz-adv-x="1000" /></svg>');
        }
        // lodgesglamping
        :global(.map-view-pin-icon.lodgesglamping) {
          background-position: center;
          transform: rotate(-45deg);
          height: 20px;
          width: 20px;
          background-image: url('data:image/svg+xml,<svg width="1024"       height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M512 128L150 768h724L512 128zM360 768V512L512 256l152 256v256H360zM480 768V512h64v256h-64z" stroke="white" stroke-width="80" stroke-linecap="round" stroke-linejoin="round"/></svg>');
        }
        // pubsinns
        :global(.map-view-pin-icon.pubsinns) {
          background-position: center;
          height: 20px;
          width: 20px;
          background-image: url('data:image/svg+xml,<svg width="1024" height="1024" viewBox="0 0 1024 1024" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill="white" d="M312 300h400c55 0 100 45 100 100v400c0 55-45 100-100 100H312c-55 0-100-45-100-100V400c0-55 45-100 100-100zm50 100v300h300V400H362zm400 50h50c28 0 50 22 50 50v100c0 28-22 50-50 50h-50V450zm-400-50h300v50H362v-50zm0 100h300v50H362v-50zm0 100h300v50H362v-50z" horiz-adv-x="1000" /></svg>');
        }

        :global(.map-view-price) {
          bottom: -20px;
          position: absolute;
          display: block;
          padding: 0.15rem 0.6rem;
          border-radius: 1rem;
          background-color: white;
          color: ${theme.colors.primary};
          min-width: 4rem;
          text-align: center;
          transform: translate(50%) rotate(-45deg);
          font-size: 12px;
          font-weight: 600;
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
        }
        :global(.map-view-pin-img) {
          transform: rotate(-45deg);
          width: 1.8rem;
          height: 1.8rem;
          object-fit: contain;
        }
        :global(.selected .map-view-pin) {
          background-color: ${theme.commoncolors.blue};
          color: ${theme.colors.secondary};
        }
        :global(.selected .map-view-price) {
          color: ${theme.commoncolors.blue};
        }
        :global(.map-view__deal-popup) {
          margin: 0;
        }
        :global(.leaflet-popup-content) {
          width: max-content;
          margin: 0;
        }
        :global(.leaflet-popup-content-wrapper) {
          padding: 0;
        }
        :global(.leaflet-popup-tip-container) {
          display: none;
        }
        :global(.leaflet-marker-icon) {
          height: 0 !important;
          width: 0 !important;
          background: none;
          border: none;
          margin: 0 !important;
        }
        :global(.leaflet-popup) {
          margin-bottom: 60px !important;
        }
        :global(.leaflet-popup p) {
          margin: 0;
        }
        :global(.leaflet-container a.leaflet-popup-close-button) {
          top: 0.8rem;
          right: 0.8rem;
          border-radius: 50%;
          background-color: ${theme.colors.background};
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
          color: ${theme.colors.headingtext};
        }
        :global(.leaflet-container a.leaflet-popup-close-button:hover) {
          color: ${theme.colors.headingtext};
        }
        .carousel-container__hidden {
          visibility: hidden;
        }
        .results-modal {
          position: absolute;
          top: 80%;
          background-color: #0000eb;
          padding: 12px;
          border-radius: 12px;
          z-index: 1000;
          display: flex;
          opacity: 0.6;
          left: 50%;
          min-width: 340px;
          transform: translateX(-50%);
        }
        .results-modal-text {
          color: white;
          margin: 0;
        }
        .results-modal-link {
          color: white;
          cursor: pointer;
          text-decoration: underline;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          :global(.leaflet-popup-content) {
            width: max-content !important;
          }
          :global(.wow-map) {
            display: block;
            height: calc(100vh - 292px);
            margin: 15px 15px 0 15px;
            position: relative;
          }
        }
        .message-container {
          position: absolute;
          top: 2rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 500;
        }
      `}</style>
    </>
  );
};

MapViewComponent.propTypes = {
  categoryFilters: PropTypes.array,
  centerLat: PropTypes.string,
  centerLon: PropTypes.string,
  isErrorApi: PropTypes.bool,
  isLoadCategory: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMissingFilter: PropTypes.bool,
  isMobile: PropTypes.bool,
  isNoResults: PropTypes.bool,
  isSlowApi: PropTypes.bool,
  pageSize: PropTypes.string,
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      latLon: PropTypes.object,
      stacked: PropTypes.bool,
      title: PropTypes.string,
    }),
  ),
  selectedCategories: PropTypes.array,
  sliderValues: PropTypes.array,
  stackedPins: PropTypes.arrayOf(
    PropTypes.shape({
      lat: PropTypes.number,
      lon: PropTypes.number,
    }),
  ),
  updateZoom: PropTypes.func,
};

MapViewComponent.defaultProps = {
  pins: [],
};

export default MapViewComponent;
